import { css } from '@emotion/react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { FC, useState } from 'react';

import { useRequestHiringGraphic } from '@features/boostMyJobs/hooks/useRequestHiringGraphic';
import { staticFileLocation } from '@server/constants';
import { enterpriseHiringGraphic } from '@features/boostMyJobs/hooks/enterpriseHiringGraphic';
import { Close } from '@mui/icons-material';
import { GraphicsIcon } from '../../../../vectors/index';

type NeedCustomHiringGraphicModalProps = {
  isOpen: boolean;
  selectedCompanyId?: string | number;
  handleClose: () => void;
  handleRequestGraphicSuccess: () => void;
  handleUploadImage: () => void;
  handleContinueWithout: () => void;
};

const NeedCustomHiringGraphicModal: FC<NeedCustomHiringGraphicModalProps> = ({
  isOpen,
  selectedCompanyId,
  handleClose,
  handleRequestGraphicSuccess,
  handleUploadImage,
  handleContinueWithout,
}) => {
  const [errorText, setErrorText] = useState('');
  const { requestHiringGraphic, isRequestHiringGraphicLoading } =
    useRequestHiringGraphic();

  const { requestGraphinc, isRequestGraphincLoading } =
    enterpriseHiringGraphic({ setErrorText });

  const handleRequestHiringGraphic = () => {
    if (selectedCompanyId) {
      requestGraphinc({ company_id: selectedCompanyId }, { onSuccess: handleRequestGraphicSuccess })
    } else
      requestHiringGraphic(undefined, { onSuccess: handleRequestGraphicSuccess })
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <IconButton
          sx={{ position: 'absolute', top: 5, right: 5 }}
          onClick={handleClose}
        >
          <Close />
        </IconButton>
        <br />
        <GraphicsIcon />
        <Typography variant="h4" sx={{ pt: 3 }}>
          Need a Custom Hiring Graphic?
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Grid sx={{ marginBottom: 2 }} container spacing={4}>
          <Grid
            item
            xs={12}
            sm={6}
            display="flex"
            alignItems="center"
            justifyContent={{ xs: 'center', sm: 'flex-start' }}
          >
            <img
              src={`${staticFileLocation}img/graphic-img-edit.png`}
              alt="Hiring Graphic Example"
              css={css`
                width: 100%;
                display: block;
                max-width: 400px;
                border-radius:8px;
              `}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Stack spacing={2} textAlign="center">
              <Typography variant="h3" textTransform="uppercase">
                Request a Custom Hiring Graphic Here!
              </Typography>
              <Typography variant="body1" component="p">
                We'll design it with your logo and images, and upload it to your
                account so you can post to facebook or anywhere on the web in
                seconds to attract more applications!
              </Typography>
              <Button
                variant="contained"
                color="secondary"
                disabled={isRequestHiringGraphicLoading}
                onClick={handleRequestHiringGraphic}
              >
                Request Your Custom Graphic
              </Button>
              <Button
                variant="contained"
                color="success"
                disabled={isRequestHiringGraphicLoading}
                onClick={handleUploadImage}
              >
                Upload Image
              </Button>
              <Button
                variant="contained"
                color="warning"
                disabled={isRequestHiringGraphicLoading}
                onClick={handleContinueWithout}
              >
                Continue Without Image
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default NeedCustomHiringGraphicModal;
